import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiTrash2, FiAlertTriangle, FiBox } from 'react-icons/fi';

interface DeleteInventoryItemModalProps {
  isOpen: boolean;
  deletingInventoryItem: any;
  onClose: () => void;
  onConfirm: () => void;
  isDeleting?: boolean;
}

export const DeleteInventoryItemModal: React.FC<DeleteInventoryItemModalProps> = ({
  isOpen,
  deletingInventoryItem,
  onClose,
  onConfirm,
  isDeleting,
}) => {
  return (
    <AnimatePresence>
      {isOpen && deletingInventoryItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={isDeleting ? undefined : onClose}
            className="absolute inset-0 bg-black/80 backdrop-blur-md"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="relative w-full max-w-md bg-[#0F0F0F] border border-white/10 rounded-[32px] p-6 overflow-hidden z-10 text-center shadow-[0_20px_50px_rgba(0,0,0,0.8)]"
          >
            <div className="flex justify-end mb-2">
              <button
                onClick={onClose}
                disabled={isDeleting}
                className="p-1.5 rounded-full hover:bg-white/5 text-zinc-400 hover:text-white cursor-pointer transition-colors disabled:opacity-50"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>

            {/* Trash Icon */}
            <div className="w-12 h-12 rounded-full bg-[#3B1216] border border-[#DC2626]/30 text-[#F87171] flex items-center justify-center mx-auto mb-4">
              <FiTrash2 className="w-5 h-5" />
            </div>

            <h3 className="text-lg font-bold text-white mb-1">Delete Inventory Item?</h3>

            {/* Warning Banner */}
            <div className="bg-[#291B00] border border-[#D97706]/40 text-[#FBBF24] text-xs p-3.5 rounded-2xl flex items-center gap-3 text-left my-4">
              <FiAlertTriangle className="w-5 h-5 text-[#FBBF24] shrink-0" />
              <span>
                This action cannot be undone. The item will be removed from the project inventory
                permanently.
              </span>
            </div>

            {/* Item Card Preview */}
            <div className="bg-[#141414] border border-white/5 rounded-2xl p-3.5 flex items-center gap-3.5 text-left mb-6">
              <div className="w-9 h-9 rounded-xl bg-zinc-900 border border-white/5 flex items-center justify-center text-zinc-400 shrink-0">
                <FiBox className="w-4 h-4" />
              </div>
              <div>
                <h4 className="text-white text-xs font-bold">{deletingInventoryItem.name}</h4>
                <p className="text-zinc-500 text-[11px] mt-0.5">
                  {deletingInventoryItem.category} · Condition: {deletingInventoryItem.condition}
                </p>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={isDeleting}
                className="w-1/2 py-2.5 border border-white/15 hover:bg-white/5 text-white rounded-full text-xs font-semibold transition-all cursor-pointer disabled:opacity-50"
              >
                Keep Item
              </button>
              <button
                type="button"
                onClick={onConfirm}
                disabled={isDeleting}
                className="w-1/2 py-2.5 bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 rounded-full text-xs font-bold transition-all cursor-pointer active:scale-95 flex items-center justify-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <span className="w-3 h-3 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                    <span>Deleting...</span>
                  </>
                ) : (
                  <span>Delete Item</span>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
